import React, { useState } from 'react'
import {
  Lightbulb,
  ChevronLeft,
  ChevronRight,
  Plus,
  Filter,
  AlertCircle,
  CheckCircle2,
  Clock,
  XCircle,
  Pause,
} from 'lucide-react'
import { usePagination } from '@/hooks/use-pagination'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { SearchWithRecent } from '@/components/ui/search-with-recent'
import { Badge } from '@/components/ui/badge'
import { formatDate } from '@/lib/utils'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useToast } from '@/hooks/use-toast'
import {
  useEnhancementRequests,
  useEnhancementRequestStats,
  useCreateEnhancementRequest,
  useUpdateEnhancementRequest,
  useDeleteEnhancementRequest,
} from '@/hooks/use-api'
import { GRACKLE_COLORS, CATEGORICAL } from '@/lib/theme'

interface EnhancementRequest {
  id: string
  title: string
  description: string | null
  category: string | null
  priority: 'Low' | 'Medium' | 'High' | 'Critical'
  status: 'New' | 'Under Review' | 'Planned' | 'In Progress' | 'Completed' | 'Rejected' | 'On Hold'
  requested_by: string | null
  assigned_to: string | null
  target_version: string | null
  created_date: string
  last_modified_date: string
  requested_by_name?: string
  requested_by_email?: string
  assigned_to_name?: string
  assigned_to_email?: string
}

const EnhancementTracker: React.FC = () => {
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [priorityFilter, setPriorityFilter] = useState<string>('all')
  const { page, firstPage, prevPage, nextPage, extract } = usePagination()
  const [createModalOpen, setCreateModalOpen] = useState(false)
  const [editingRequest, setEditingRequest] = useState<EnhancementRequest | null>(null)
  const [viewingRequest, setViewingRequest] = useState<EnhancementRequest | null>(null)

  const { toast } = useToast()

  // Fetch enhancement requests using hooks
  const { data, isLoading, error } = useEnhancementRequests({
    search,
    status: statusFilter !== 'all' ? statusFilter : undefined,
    priority: priorityFilter !== 'all' ? priorityFilter : undefined,
    page,
    limit: 25,
  })

  // Fetch stats
  const { data: stats } = useEnhancementRequestStats()

  // Mutations
  const createMutation = useCreateEnhancementRequest()
  const updateMutation = useUpdateEnhancementRequest()
  const deleteMutation = useDeleteEnhancementRequest()

  // Handle create success
  React.useEffect(() => {
    if (createMutation.isSuccess) {
      setCreateModalOpen(false)
      toast({
        title: 'Success',
        description: 'Enhancement request created successfully',
      })
    }
  }, [createMutation.isSuccess, toast])

  // Handle create error
  React.useEffect(() => {
    if (createMutation.isError) {
      toast({
        title: 'Error',
        description:
          createMutation.error instanceof Error
            ? createMutation.error.message
            : 'Failed to create enhancement request',
        variant: 'destructive',
      })
    }
  }, [createMutation.isError, createMutation.error, toast])

  // Handle update success
  React.useEffect(() => {
    if (updateMutation.isSuccess) {
      setEditingRequest(null)
      setViewingRequest(null)
      toast({
        title: 'Success',
        description: 'Enhancement request updated successfully',
      })
    }
  }, [updateMutation.isSuccess, toast])

  // Handle update error
  React.useEffect(() => {
    if (updateMutation.isError) {
      toast({
        title: 'Error',
        description:
          updateMutation.error instanceof Error
            ? updateMutation.error.message
            : 'Failed to update enhancement request',
        variant: 'destructive',
      })
    }
  }, [updateMutation.isError, updateMutation.error, toast])

  // Handle delete success
  React.useEffect(() => {
    if (deleteMutation.isSuccess) {
      setViewingRequest(null)
      toast({
        title: 'Success',
        description: 'Enhancement request deleted successfully',
      })
    }
  }, [deleteMutation.isSuccess, toast])

  // Handle delete error
  React.useEffect(() => {
    if (deleteMutation.isError) {
      toast({
        title: 'Error',
        description:
          deleteMutation.error instanceof Error
            ? deleteMutation.error.message
            : 'Failed to delete enhancement request',
        variant: 'destructive',
      })
    }
  }, [deleteMutation.isError, deleteMutation.error, toast])

  const {
    items: requests,
    total,
    currentPage,
    limit,
    totalPages,
    hasNextPage,
    hasPrevPage,
  } = extract(data)

  const getStatusBadge = (status: string) => {
    const variants: Record<string, { icon: React.ReactNode; variant: string; text: string }> = {
      New: { icon: <AlertCircle className="w-3 h-3 mr-1" />, variant: 'default', text: 'New' },
      'Under Review': {
        icon: <Clock className="w-3 h-3 mr-1" />,
        variant: 'secondary',
        text: 'Under Review',
      },
      Planned: { icon: <Clock className="w-3 h-3 mr-1" />, variant: 'secondary', text: 'Planned' },
      'In Progress': {
        icon: <Clock className="w-3 h-3 mr-1" />,
        variant: 'default',
        text: 'In Progress',
      },
      Completed: {
        icon: <CheckCircle2 className="w-3 h-3 mr-1" />,
        variant: 'success',
        text: 'Completed',
      },
      Rejected: {
        icon: <XCircle className="w-3 h-3 mr-1" />,
        variant: 'destructive',
        text: 'Rejected',
      },
      'On Hold': {
        icon: <Pause className="w-3 h-3 mr-1" />,
        variant: 'secondary',
        text: 'On Hold',
      },
    }

    const config = variants[status] ?? variants['New']
    return (
      <Badge variant={config?.variant as any} className="flex items-center">
        {config?.icon}
        {config?.text}
      </Badge>
    )
  }

  const getPriorityBadge = (priority: string) => {
    const styles: Record<string, { backgroundColor: string; color: string; borderColor: string }> =
      {
        Critical: {
          backgroundColor: CATEGORICAL.raspberry.tint,
          color: GRACKLE_COLORS.prussianBlue,
          borderColor: CATEGORICAL.raspberry.solid,
        },
        High: {
          backgroundColor: CATEGORICAL.clay.tint,
          color: GRACKLE_COLORS.prussianBlue,
          borderColor: CATEGORICAL.clay.solid,
        },
        Medium: {
          backgroundColor: CATEGORICAL.pollen.tint,
          color: GRACKLE_COLORS.prussianBlue,
          borderColor: CATEGORICAL.pollen.solid,
        },
        Low: {
          backgroundColor: GRACKLE_COLORS.paleGrey,
          color: GRACKLE_COLORS.navy,
          borderColor: CATEGORICAL.slate.solid,
        },
      }

    const style = styles[priority] || styles['Medium']
    return <Badge style={style}>{priority}</Badge>
  }

  return (
    <main className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-3">
            <div
              className="inline-flex items-center gap-1.5 px-3 py-1 rounded-md text-sm font-medium border"
              style={{
                backgroundColor: CATEGORICAL.plum.tint,
                color: GRACKLE_COLORS.prussianBlue,
                borderColor: CATEGORICAL.plum.solid,
              }}
            >
              <Lightbulb className="h-3.5 w-3.5" aria-hidden="true" />
              <span>Enhancement Tracker</span>
            </div>
            <h1 className="text-3xl font-bold tracking-tight">Enhancement Tracker</h1>
          </div>
          <p className="text-muted-foreground mt-1">
            Track and manage enhancement requests for Grackle CRM
          </p>
        </div>
        <Button
          aria-label="Create new enhancement request"
          onClick={() => setCreateModalOpen(true)}
        >
          <Plus className="mr-2 h-4 w-4" aria-hidden="true" />
          New Request
        </Button>
      </div>

      {/* Summary Cards */}
      {stats && (
        <div className="grid gap-2 md:grid-cols-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-0">
              <CardTitle className="text-sm font-medium">Total Requests</CardTitle>
              <Lightbulb className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
            </CardHeader>
            <CardContent className="p-3">
              <div className="text-xl font-bold leading-none">{stats.total}</div>
              <p className="text-xs text-muted-foreground leading-tight">
                All enhancement requests
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-0">
              <CardTitle className="text-sm font-medium">In Progress</CardTitle>
              <Clock className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
            </CardHeader>
            <CardContent className="p-3">
              <div className="text-xl font-bold leading-none">{stats.in_progress}</div>
              <p className="text-xs text-muted-foreground leading-tight">
                Currently being worked on
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-0">
              <CardTitle className="text-sm font-medium">Completed</CardTitle>
              <CheckCircle2 className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
            </CardHeader>
            <CardContent className="p-3">
              <div className="text-xl font-bold leading-none">{stats.completed}</div>
              <p className="text-xs text-muted-foreground leading-tight">Successfully completed</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-0">
              <CardTitle className="text-sm font-medium">Critical Priority</CardTitle>
              <AlertCircle className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
            </CardHeader>
            <CardContent className="p-3">
              <div className="text-xl font-bold leading-none">{stats.critical}</div>
              <p className="text-xs text-muted-foreground leading-tight">
                Requires immediate attention
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters and Search */}
      <Card>
        <CardHeader>
          <CardTitle>Enhancement Requests</CardTitle>
          <CardDescription>Search and filter enhancement requests</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col sm:flex-row gap-4">
            <SearchWithRecent
              value={search}
              onChange={(v) => {
                setSearch(v)
                firstPage()
              }}
              placeholder="Search by title or description..."
              ariaLabel="Search enhancement requests"
              entityKey="enhancements"
              className="flex-1"
            />
            <Select
              value={statusFilter}
              onValueChange={(value) => {
                setStatusFilter(value)
                firstPage()
              }}
            >
              <SelectTrigger className="w-full sm:w-[180px]" aria-label="Filter by status">
                <Filter className="h-4 w-4 mr-2" aria-hidden="true" />
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="New">New</SelectItem>
                <SelectItem value="Under Review">Under Review</SelectItem>
                <SelectItem value="Planned">Planned</SelectItem>
                <SelectItem value="In Progress">In Progress</SelectItem>
                <SelectItem value="Completed">Completed</SelectItem>
                <SelectItem value="Rejected">Rejected</SelectItem>
                <SelectItem value="On Hold">On Hold</SelectItem>
              </SelectContent>
            </Select>
            <Select
              value={priorityFilter}
              onValueChange={(value) => {
                setPriorityFilter(value)
                firstPage()
              }}
            >
              <SelectTrigger className="w-full sm:w-[180px]" aria-label="Filter by priority">
                <Filter className="h-4 w-4 mr-2" aria-hidden="true" />
                <SelectValue placeholder="Priority" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Priorities</SelectItem>
                <SelectItem value="Critical">Critical</SelectItem>
                <SelectItem value="High">High</SelectItem>
                <SelectItem value="Medium">Medium</SelectItem>
                <SelectItem value="Low">Low</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Requests Table */}
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-8 text-center text-muted-foreground">
              Loading enhancement requests...
            </div>
          ) : error ? (
            <div className="p-8 text-center text-destructive">
              Error loading enhancement requests
            </div>
          ) : requests.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground">
              No enhancement requests found
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full" role="table" aria-label="Enhancement requests table">
                  <thead className="border-b bg-muted/50">
                    <tr>
                      <th
                        scope="col"
                        className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider"
                      >
                        Title
                      </th>
                      <th
                        scope="col"
                        className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider"
                      >
                        Category
                      </th>
                      <th
                        scope="col"
                        className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider"
                      >
                        Priority
                      </th>
                      <th
                        scope="col"
                        className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider"
                      >
                        Status
                      </th>
                      <th
                        scope="col"
                        className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider"
                      >
                        Created
                      </th>
                      <th
                        scope="col"
                        className="px-4 py-3 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider"
                      >
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {requests.map((request: EnhancementRequest) => (
                      <tr key={request.id} className="hover:bg-muted/50">
                        <td className="px-4 py-3">
                          <div className="font-medium">{request.title}</div>
                          {request.description && (
                            <div className="text-sm text-muted-foreground line-clamp-1">
                              {request.description}
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-sm">{request.category || '-'}</span>
                        </td>
                        <td className="px-4 py-3">{getPriorityBadge(request.priority)}</td>
                        <td className="px-4 py-3">{getStatusBadge(request.status)}</td>
                        <td className="px-4 py-3 text-sm text-muted-foreground">
                          {formatDate(request.created_date)}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setViewingRequest(request)}
                            aria-label={`View details for ${request.title}`}
                          >
                            View
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between px-4 py-3 border-t">
                  <div className="text-sm text-muted-foreground">
                    Showing {(currentPage - 1) * limit + 1} to{' '}
                    {Math.min(currentPage * limit, total)} of {total} requests
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={prevPage}
                      disabled={!hasPrevPage}
                      aria-label="Previous page"
                    >
                      <ChevronLeft className="h-4 w-4" aria-hidden="true" />
                      Previous
                    </Button>
                    <div className="text-sm">
                      Page {currentPage} of {totalPages}
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={nextPage}
                      disabled={!hasNextPage}
                      aria-label="Next page"
                    >
                      Next
                      <ChevronRight className="h-4 w-4" aria-hidden="true" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Modal */}
      <CreateEditModal
        open={createModalOpen || !!editingRequest}
        onClose={() => {
          setCreateModalOpen(false)
          setEditingRequest(null)
        }}
        request={editingRequest}
        onSubmit={(data) => {
          if (editingRequest) {
            updateMutation.mutate({ id: editingRequest.id, data })
          } else {
            createMutation.mutate(data)
          }
        }}
        isSubmitting={createMutation.isPending || updateMutation.isPending}
      />

      {/* View Modal */}
      <ViewModal
        open={!!viewingRequest}
        onClose={() => setViewingRequest(null)}
        request={viewingRequest}
        onEdit={() => {
          setEditingRequest(viewingRequest)
          setViewingRequest(null)
        }}
        onDelete={(id) => deleteMutation.mutate(id)}
        isDeleting={deleteMutation.isPending}
      />
    </main>
  )
}

// Create/Edit Modal Component
interface CreateEditModalProps {
  open: boolean
  onClose: () => void
  request: EnhancementRequest | null
  onSubmit: (data: Partial<EnhancementRequest>) => void
  isSubmitting: boolean
}

const CreateEditModal: React.FC<CreateEditModalProps> = ({
  open,
  onClose,
  request,
  onSubmit,
  isSubmitting,
}) => {
  const { toast } = useToast()
  const [formData, setFormData] = useState<Partial<EnhancementRequest>>({
    title: '',
    description: '',
    category: '',
    priority: 'Medium',
    status: 'New',
    target_version: '',
  })
  const [formErrors, setFormErrors] = useState<Record<string, string>>({})

  React.useEffect(() => {
    if (request) {
      setFormData({
        title: request.title,
        description: request.description || '',
        category: request.category || '',
        priority: request.priority,
        status: request.status,
        target_version: request.target_version || '',
      })
    } else {
      setFormData({
        title: '',
        description: '',
        category: '',
        priority: 'Medium',
        status: 'New',
        target_version: '',
      })
    }
    setFormErrors({})
  }, [request, open])

  const handleFieldChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
    if (formErrors[field]) {
      setFormErrors((prev) => {
        const next = { ...prev }
        delete next[field]
        return next
      })
    }
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    const errors: Record<string, string> = {}
    if (!formData.title?.trim()) errors.title = 'Title is required'

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors)
      toast({
        title: 'Validation Error',
        description: 'Please fill in all required fields.',
        variant: 'destructive',
      })
      return
    }
    onSubmit(formData)
  }

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {request ? 'Edit Enhancement Request' : 'Create Enhancement Request'}
          </DialogTitle>
          <DialogDescription>
            {request
              ? 'Update the enhancement request details'
              : 'Add a new enhancement request to the tracker'}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="title">Title *</Label>
              <Input
                id="title"
                value={formData.title}
                onChange={(e) => handleFieldChange('title', e.target.value)}
                maxLength={255}
                aria-required="true"
                aria-invalid={!!formErrors.title}
                aria-describedby={formErrors.title ? 'title-error' : undefined}
              />
              {formErrors.title && (
                <p id="title-error" className="text-sm text-destructive mt-1" role="alert">
                  {formErrors.title}
                </p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={formData.description || ''}
                onChange={(e) => handleFieldChange('description', e.target.value)}
                rows={4}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="category">Category</Label>
                <Input
                  id="category"
                  value={formData.category || ''}
                  onChange={(e) => handleFieldChange('category', e.target.value)}
                  placeholder="e.g., UI/UX, Performance"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="target_version">Target Version</Label>
                <Input
                  id="target_version"
                  value={formData.target_version || ''}
                  onChange={(e) => handleFieldChange('target_version', e.target.value)}
                  placeholder="e.g., 2.0"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="priority">Priority</Label>
                <Select
                  value={formData.priority}
                  onValueChange={(value: any) => setFormData({ ...formData, priority: value })}
                >
                  <SelectTrigger id="priority">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Low">Low</SelectItem>
                    <SelectItem value="Medium">Medium</SelectItem>
                    <SelectItem value="High">High</SelectItem>
                    <SelectItem value="Critical">Critical</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="status">Status</Label>
                <Select
                  value={formData.status}
                  onValueChange={(value: any) => setFormData({ ...formData, status: value })}
                >
                  <SelectTrigger id="status">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="New">New</SelectItem>
                    <SelectItem value="Under Review">Under Review</SelectItem>
                    <SelectItem value="Planned">Planned</SelectItem>
                    <SelectItem value="In Progress">In Progress</SelectItem>
                    <SelectItem value="Completed">Completed</SelectItem>
                    <SelectItem value="Rejected">Rejected</SelectItem>
                    <SelectItem value="On Hold">On Hold</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Saving...' : request ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

// View Modal Component
interface ViewModalProps {
  open: boolean
  onClose: () => void
  request: EnhancementRequest | null
  onEdit: () => void
  onDelete: (id: string) => void
  isDeleting: boolean
}

const ViewModal: React.FC<ViewModalProps> = ({
  open,
  onClose,
  request,
  onEdit,
  onDelete,
  isDeleting,
}) => {
  if (!request) return null

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{request.title}</DialogTitle>
          <DialogDescription>Enhancement request details</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="flex gap-2">
            <Badge variant="outline">{request.priority}</Badge>
            <Badge variant="outline">{request.status}</Badge>
            {request.category && <Badge variant="outline">{request.category}</Badge>}
          </div>
          {request.description && (
            <div>
              <h4 className="font-medium mb-2">Description</h4>
              <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                {request.description}
              </p>
            </div>
          )}
          <div className="grid grid-cols-2 gap-4 text-sm">
            {request.target_version && (
              <div>
                <span className="font-medium">Target Version:</span> {request.target_version}
              </div>
            )}
            <div>
              <span className="font-medium">Created:</span> {formatDate(request.created_date)}
            </div>
            {request.requested_by_name && (
              <div>
                <span className="font-medium">Requested By:</span> {request.requested_by_name}
              </div>
            )}
            {request.assigned_to_name && (
              <div>
                <span className="font-medium">Assigned To:</span> {request.assigned_to_name}
              </div>
            )}
          </div>
        </div>
        <DialogFooter className="flex justify-between">
          <Button variant="destructive" onClick={() => onDelete(request.id)} disabled={isDeleting}>
            {isDeleting ? 'Deleting...' : 'Delete'}
          </Button>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>
              Close
            </Button>
            <Button onClick={onEdit}>Edit</Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default EnhancementTracker
